import React, {
    useEffect,
    useRef,
    useState,
    useLayoutEffect,
    useMemo,
} from "react";
import "../css/Map.css";
import axios from "axios";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import * as topojson from "topojson-client";
import SlidingRadioTabs from "./SlidingRadioTabs";

// 디바운스 속도 설정 (조절 가능)
const DEBOUNCE_CONFIG = {
    BOUNDS_CHANGED: 100,    // 지도 이동 시 디바운스 (ms)
    ZOOM_CHANGED: 100,      // 줌 변경 시 디바운스 (ms)
    DATA_RENDERING: 100,    // 데이터 렌더링 시 디바운스 (ms)
};

function useSelection() {
    const [checked, setChecked] = useState(() => new Set());
    const isChecked = (k) => checked.has(k);
    const setMany = (keys, on) => {
        setChecked((prev) => {
            const next = new Set(prev);
            keys.forEach((k) => (on ? next.add(k) : next.delete(k)));
            return next;
        });
    };
    const clearAll = () => setChecked(new Set());
    return { checked, isChecked, setMany, clearAll };
}

const Map = ({ content }) => {
    const [isPanelOpen, setIsPanelOpen] = useState(true);
    const [dockOpen, setDockOpen] = useState(true);
    
    // newData 폴더의 CSV 파일들 상태 (모든 데이터를 newData에서 로드)
    const [newDataFiles, setNewDataFiles] = useState({}); // newData 폴더의 CSV 파일들 상태
    const [isDataLoading, setIsDataLoading] = useState(true); // 데이터 로딩 상태
    const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0, currentFile: '' }); // 로딩 진행 상태
    
    // Map copy.js에서 가져온 spatialInfoOptions 데이터 구조 - useMemo로 다국어 지원
    const spatialInfoOptions = useMemo(() => {
        const options = [];
        
        // newData 폴더의 파일들을 옵션에 추가 (파일명을 데이터명으로 사용)
        const newDataKeys = Object.keys(newDataFiles);
        if (newDataKeys.length > 0) {
            console.log('newDataFiles keys:', newDataKeys);
            console.log('Total spatialInfoOptions before newData:', options.length);
            
            newDataKeys.forEach((fileName) => {
                const fileData = newDataFiles[fileName];
                if (fileData && Object.keys(fileData).length > 0) {
                    const fileId = `newdata_${fileName.replace(/[^a-zA-Z0-9가-힣]/g, '_')}`;
                    
                    // 행정구역 표준 순서 정의
                    const regionOrder = [
                        '서울특별시',
                        '부산광역시',
                        '대구광역시',
                        '인천광역시',
                        '광주광역시',
                        '대전광역시',
                        '울산광역시',
                        '세종특별자치시',
                        '경기도',
                        '강원특별자치도',
                        '충청북도',
                        '충청남도',
                        '전북특별자치도',
                        '전라남도',
                        '경상북도',
                        '경상남도',
                        '제주특별자치도',
                        '기타' // 기타는 마지막
                    ];
                    
                    // 카테고리를 표준 순서로 정렬
                    const sortedCategories = Object.keys(fileData)
                        .sort((a, b) => {
                            const indexA = regionOrder.indexOf(a);
                            const indexB = regionOrder.indexOf(b);
                            // 순서에 없는 경우(없어야 하지만)는 마지막에 배치
                            if (indexA === -1) return 1;
                            if (indexB === -1) return -1;
                            return indexA - indexB;
                        })
                        .map(cityName => ({
                            id: `${fileId}_${cityName}`,
                            name: cityName,
                            data: fileData[cityName] || []
                        }));
                    
                    options.push({
                        id: fileId,
                        name: fileName.replace('.csv', ''), // 파일명에서 확장자 제거하여 데이터명으로 사용
                        description: `${fileName.replace('.csv', '')} 데이터입니다.`,
                        type: "marker",
                        categories: sortedCategories
                    });
                }
            });
            
            console.log('Total spatialInfoOptions after newData:', options.length);
        }
        
        return options;
    }, [content, newDataFiles]);

    const mapRef = useRef(null);
    const [mapInstance, setMapInstance] = useState(null);
    const [searchAddress, setSearchAddress] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [infoIndex, setInfoIndex] = useState(0);
    const [travelMode, setTravelMode] = useState("car");
    const [activeHeatmaps, setActiveHeatmaps] = useState([]);
    const [activeMarkerClusterers, setActiveMarkerClusterers] = useState([]);
    const [selectedRegions, setSelectedRegions] = useState({});
    const [geoJsonData, setGeoJsonData] = useState({});
    const [mobileStage, setMobileStage] = useState("home");
    const [isMobile, setIsMobile] = useState(false);
    
    // 길찾기 관련 상태
    const [directionMode, setDirectionMode] = useState(null);
    const [directionResults, setDirectionResults] = useState([]);
    const [startMarker, setStartMarker] = useState(null);
    const [endMarker, setEndMarker] = useState(null);
    const [startInfo, setStartInfo] = useState(null);
    const [endInfo, setEndInfo] = useState(null);
    const [startAddress, setStartAddress] = useState("");
    const [endAddress, setEndAddress] = useState("");
    const [activeMarkers, setActiveMarkers] = useState([]);
    const [activeLines, setActiveLines] = useState([]);
    const [routeDistance, setRouteDistance] = useState(null);
    const [routeDuration, setRouteDuration] = useState(null);
    const [routePolyline, setRoutePolyline] = useState(null);
    const [routeData, setRouteData] = useState(null);
    const [showDetails, setShowDetails] = useState(true);
    const [transitSteps, setTransitSteps] = useState([]);
    const [carSteps, setCarSteps] = useState([]);
    const [walkSteps, setWalkSteps] = useState([]);

    // Map.js의 UI 상태 관리
    const [activeTab, setActiveTab] = useState(() => content?.mapBoundaryTab || "행정경계");
    const [expandedBoundaries, setExpandedBoundaries] = useState([]);
    const [boundarySearchKeyword, setBoundarySearchKeyword] = useState(""); // 행정구역 검색 키워드
    const [filteredBoundaries, setFilteredBoundaries] = useState({}); // 필터링된 행정구역
    const [dataSearchKeyword, setDataSearchKeyword] = useState(""); // 데이터 검색 키워드
    const [filteredSpatialOptions, setFilteredSpatialOptions] = useState([]); // 필터링된 데이터 옵션
    const { checked, isChecked, setMany, clearAll } = useSelection();

    // Map copy.js의 공간정보 데이터 상태 관리
    const [openSpatialData, setOpenSpatialData] = useState(() => new Set());
    const [spatialDataState, setSpatialDataState] = useState(() =>
        Object.fromEntries(
            spatialInfoOptions.map((s) => [
                s.id,
                { mode: s.type === "marker" ? "POI" : "Heatmap", selected: new Set() },
            ])
        )
    );
    const [appliedSpatialDataState, setAppliedSpatialDataState] = useState(() =>
        Object.fromEntries(
            spatialInfoOptions.map((s) => [
                s.id,
                { mode: s.type === "marker" ? "POI" : "Heatmap", selected: new Set() },
            ])
        )
    );

    // spatialInfoOptions가 변경될 때 상태 초기화 (새로운 옵션 추가 시)
    useEffect(() => {
        if (spatialInfoOptions.length === 0) return;
        
        setSpatialDataState((prev) => {
            const newState = {};
            spatialInfoOptions.forEach((s) => {
                // 기존 상태가 있으면 유지, 없으면 새로 생성
                newState[s.id] = prev[s.id] || { 
                    mode: s.type === "marker" ? "POI" : "Heatmap", 
                    selected: new Set() 
                };
            });
            return newState;
        });
        setAppliedSpatialDataState((prev) => {
            const newState = {};
            spatialInfoOptions.forEach((s) => {
                // 기존 상태가 있으면 유지, 없으면 새로 생성
                newState[s.id] = prev[s.id] || { 
                    mode: s.type === "marker" ? "POI" : "Heatmap", 
                    selected: new Set() 
                };
            });
            return newState;
        });
    }, [spatialInfoOptions]);

    const boundaryOptions = [
        { id: "sido", name: content?.mapBoundarySido || "시도경계", url: "/mapData/korea_sido.json" },
        { id: "sigungu", name: content?.mapBoundarySigungu || "시군구경계", url: "/mapData/korea_sigungu.json" },
        { id: "beopjeongdong", name: content?.mapBoundaryBeopjeongdong || "법정동경계", url: "/mapData/korea_beopjeongdong.json" },
        { id: "haengjeongdong", name: content?.mapBoundaryHaengjeongdong || "행정동경계", url: "/mapData/korea_haengjeongdong.json" },
    ];

    // Map.js의 메뉴 순서 (공간정보 → 길찾기 → 위치검색)
    const infoMenuList = [
        {
            id: "layers",
            title: content?.mapSpatialInfo || "공간정보",
            content: content?.mapSpatialInfoDesc || "공간 관련 정보를 제공합니다.",
        },
        {
            id: "directions", 
            title: content?.mapDirections || "길찾기",
            content: content?.mapDirectionsDesc || "출발지와 목적지를 설정하세요.",
        },
        {
            id: "search",
            title: content?.mapSearch || "위치검색",
            content: content?.mapSearchDesc || "장소를 검색할 수 있습니다.",
        },
    ];

    // Map copy.js의 공간정보 관련 함수들
    const toggleOpenSpatialData = (id) =>
        setOpenSpatialData((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const setSpatialMode = (id, mode) =>
        setSpatialDataState((st) => ({ ...st, [id]: { ...st[id], mode } }));

    const toggleSpatialCategory = (id, categoryId, on) =>
        setSpatialDataState((st) => {
            const next = new Set(st[id].selected);
            on ? next.add(categoryId) : next.delete(categoryId);
            return { ...st, [id]: { ...st[id], selected: next } };
        });

    const spatialAllState = (secId) => {
        const categories = spatialInfoOptions.find((s) => s.id === secId)?.categories || [];
        const total = categories.length;
        
        // 상태가 없거나 categories가 비어있으면 false 반환
        if (!spatialDataState[secId] || total === 0) {
            return {
                all: false,
                some: false,
            };
        }
        
        const count = categories.filter((c) =>
            spatialDataState[secId].selected.has(c.id)
        ).length;
        return {
            all: count === total && total > 0,
            some: count > 0 && count < total,
        };
    };

    const toggleAllSpatialCategories = (secId, on) =>
        setSpatialDataState((st) => {
            const categories = spatialInfoOptions.find((s) => s.id === secId)?.categories || [];
            
            // 상태가 없으면 초기화
            if (!st[secId]) {
                const spatialOption = spatialInfoOptions.find((s) => s.id === secId);
                st[secId] = { 
                    mode: spatialOption?.type === "marker" ? "POI" : "Heatmap", 
                    selected: new Set() 
                };
            }
            
            return {
                ...st,
                [secId]: { 
                    ...st[secId], 
                    selected: new Set(on ? categories.map(c => c.id) : [])
                },
            };
        });

    // 행정구역 키워드 검색 함수
    const searchBoundaries = (keyword) => {
        if (!keyword.trim()) {
            setFilteredBoundaries({});
            return;
        }

        // regionList가 아직 로드되지 않았으면 검색하지 않음
        if (!regionList || Object.keys(regionList).length === 0) {
            return;
        }

        const filtered = {};
        Object.entries(regionList).forEach(([boundaryId, regions]) => {
            // regions가 배열인지 확인하고 안전하게 처리
            if (Array.isArray(regions)) {
                const matchingRegions = regions.filter(region => 
                    region && region.name && region.name.toLowerCase().includes(keyword.toLowerCase())
                );
                if (matchingRegions.length > 0) {
                    filtered[boundaryId] = matchingRegions;
                }
            }
        });

        setFilteredBoundaries(filtered);

        // 검색 결과가 있을 때만 해당 섹션 펼치고 focus (체크하지 않음)
        const firstBoundaryId = Object.keys(filtered)[0];
        if (firstBoundaryId && filtered[firstBoundaryId] && filtered[firstBoundaryId].length > 0) {
            // 해당 행정구역 섹션을 펼치기만 함 (체크하지 않음)
            setExpandedBoundaries(prev => 
                prev.includes(firstBoundaryId) ? prev : [...prev, firstBoundaryId]
            );
        }
    };

    // 데이터 검색 함수
    const searchData = (keyword) => {
        if (!keyword.trim()) {
            setFilteredSpatialOptions([]);
            return;
        }

        const filtered = spatialInfoOptions.filter(spatial => 
            spatial.name.toLowerCase().includes(keyword.toLowerCase()) ||
            spatial.description.toLowerCase().includes(keyword.toLowerCase()) ||
            spatial.categories.some(category => 
                category.name.toLowerCase().includes(keyword.toLowerCase())
            )
        );

        setFilteredSpatialOptions(filtered);

        // 검색 결과가 있을 때만 해당 섹션 펼치기
        if (filtered.length > 0) {
            filtered.forEach(spatial => {
                if (!openSpatialData.has(spatial.id)) {
                    setOpenSpatialData(prev => new Set([...prev, spatial.id]));
                }
            });
        }
    };
    useEffect(() => {
        if (!mapInstance || !routeData) return;

        const { path, distance, duration, steps = [] } = routeData;

        setRouteDistance(distance);
        setRouteDuration(duration);

        if (routePolyline) routePolyline.forEach((line) => line.setMap(null));
        activeLines.forEach((line) => line.setMap(null));
        activeMarkers.forEach((marker) => marker.setMap(null));
        setRoutePolyline(null);
        setActiveLines([]);
        setActiveMarkers([]);
        setTransitSteps([]);
        setCarSteps([]);
        setWalkSteps([]);

        const bounds = new window.google.maps.LatLngBounds();

        if (travelMode === "transit" && steps) {
            // Use travelMode from state
            const newActiveLines = [];
            const newActiveMarkers = [];

            steps.forEach((step, index) => {
                let strokeColor;
                let strokeWeight = 5;
                let icons = [];

                if (step.travel_mode === "WALKING") {
                    strokeColor = "#FBAF32";
                    strokeWeight = 5;
                    icons = [
                        {
                            icon: {
                                path: "M 0,-1 0,1",
                                strokeOpacity: 1,
                                scale: 3,
                            },
                            offset: "0",
                            repeat: "15px",
                        },
                    ];
                } else if (step.travel_mode === "TRANSIT") {
                    if (
                        step.transit_details &&
                        step.transit_details.vehicle_type === "SUBWAY"
                    ) {
                        strokeColor = "#3070b1";
                    } else if (
                        step.transit_details &&
                        step.transit_details.vehicle_type === "BUS"
                    ) {
                        strokeColor = "#5ABF41";
                    } else {
                        strokeColor = "#6f42c1";
                    }
                } else {
                    strokeColor = "#FBAF32";
                }

                if (step.polyline) {
                    const decoded =
                        window.google.maps.geometry.encoding.decodePath(
                            step.polyline
                        );

                    const borderLine = new window.google.maps.Polyline({
                        path: decoded,
                        strokeColor: "#424242ff",
                        strokeOpacity: 1,
                        strokeWeight: (strokeWeight || 5) + 3,
                        map: mapInstance,
                        zIndex: 1,
                    });
                    newActiveLines.push(borderLine);
                    const innerLine = new window.google.maps.Polyline({
                        path: decoded,
                        strokeColor,
                        strokeOpacity: 0.8,
                        strokeWeight,
                        map: mapInstance,
                        icons,
                        zIndex: 2,
                    });
                    newActiveLines.push(innerLine);

                    const decodedStepPath =
                        window.google.maps.geometry.encoding.decodePath(
                            step.polyline
                        );

                    if (decodedStepPath.length > 0) {
                        const stepStartLocation = decodedStepPath[0];

                        const stepMarker = new window.google.maps.Marker({
                            position: stepStartLocation,
                            map: mapInstance,
                            label: {
                                text: `${index + 1}`,
                                color: "white",
                                fontWeight: "bold",
                            },
                            icon: {
                                path: window.google.maps.SymbolPath.CIRCLE,
                                scale: 10,
                                fillColor: strokeColor,
                                fillOpacity: 1,
                                strokeWeight: 1,
                                strokeColor: "#fff",
                            },
                            zIndex: 3,
                        });

                        newActiveMarkers.push(stepMarker);
                    }
                }
            });

            const transformedSteps = transformSteps(steps);
            setTransitSteps(transformedSteps);
            setActiveLines(newActiveLines);
            setActiveMarkers((prev) => [...prev, ...newActiveMarkers]);

            const decodedPathForBounds =
                window.google.maps.geometry.encoding.decodePath(path);
            decodedPathForBounds.forEach((p) => {
                const lat = p.lat();
                const lng = p.lng();
                if (
                    typeof lat === "number" &&
                    typeof lng === "number" &&
                    !isNaN(lat) &&
                    !isNaN(lng)
                ) {
                    bounds.extend(new window.google.maps.LatLng(lat, lng));
                } else {
                    console.warn(
                        `[Map.js] Invalid coordinate in decoded path (transit):`,
                        p
                    );
                }
            });
        } else {
            // 자동차 또는 도보
            const strokeColor = travelMode === "walk" ? "#FBAF32" : "#FBAF32";
            const borderColor =
                travelMode === "walk" ? "#424242ff" : "#424242ff";
            const outerLine = new window.google.maps.Polyline({
                path: path.map((p) => ({ lat: p.lat, lng: p.lng })),
                strokeColor: borderColor,
                strokeOpacity: 1,
                strokeWeight: 8, // 메인 라인보다 두꺼움
                map: mapInstance,
                zIndex: 1,
            });

            // 안쪽 메인 라인
            const innerLine = new window.google.maps.Polyline({
                path: path.map((p) => ({ lat: p.lat, lng: p.lng })),
                strokeColor: strokeColor,
                strokeOpacity: 1,
                strokeWeight: 6, // 테두리보다 살짝 얇음
                map: mapInstance,
                zIndex: 2,
            });

            setRoutePolyline([outerLine, innerLine]);
            if (travelMode === "car") {
                const mergedSteps = mergeTmapCarSteps(steps);
                setCarSteps(mergedSteps);
            } else if (travelMode === "walk") {
                const mergedSteps = mergeTmapCarSteps(steps);
                setWalkSteps(mergedSteps);
            }
            path.forEach((p) => {
                if (
                    typeof p.lat === "number" &&
                    typeof p.lng === "number" &&
                    !isNaN(p.lat) &&
                    !isNaN(p.lng)
                ) {
                    bounds.extend(new window.google.maps.LatLng(p.lat, p.lng));
                } else {
                    console.warn(
                        `[Map.js] Invalid coordinate in path (car/walk):`,
                        p
                    );
                }
            });
        }

        mapInstance.fitBounds(bounds);
    }, [mapInstance, travelMode, routeData]);
    // 초기화 및 이벤트 핸들러들
    useEffect(() => {
        const mq = window.matchMedia("(max-width: 767px)");
        const sync = () => setIsMobile(mq.matches);
        sync();
        mq.addEventListener("change", sync);
        return () => mq.removeEventListener("change", sync);
    }, []);

    useEffect(() => {
        if (isMobile) setIsPanelOpen(mobileStage !== "home");
    }, [isMobile, mobileStage]);

    // 지역 데이터 처리 - useMemo로 최적화
    const regionList = useMemo(() => {
        if (Object.keys(geoJsonData).length === 0) {
            return {};
        }
        const newRegionList = {};
        for (const boundaryId in geoJsonData) {
            const data = geoJsonData[boundaryId];
            if (data && data.features) {
                const allRegions = data.features
                    .map((feature) => {
                        const name =
                            feature.properties.EMD_NM ||  // 법정동 이름
                            feature.properties.NAME ||
                            feature.properties.SIDO_NM ||
                            feature.properties.ADM_NM;
                        const sido_cd = feature.properties.SIDO_CD || null;
                        const adm_cd = feature.properties.ADM_CD || null;
                        const bjcd = feature.properties.BJCD || null;
                        const emd_cd = feature.properties.EMD_CD || null;  // 법정동 코드
                        const id = emd_cd || bjcd || adm_cd || sido_cd;
                        if (name && id) {
                            return { id, name, sido_cd, adm_cd, bjcd, emd_cd };
                        }
                        return null;
                    })
                    .filter(Boolean);
                newRegionList[boundaryId] = allRegions;
            }
        }
        return newRegionList;
    }, [geoJsonData]);

    // 선택된 지역 렌더링 - 디바운싱으로 최적화
    useEffect(() => {
        if (!mapInstance) return;

        // 디바운스 타이머
        const timer = setTimeout(() => {
            mapInstance.data.forEach((feature) => {
                mapInstance.data.remove(feature);
            });

            Object.entries(geoJsonData).forEach(([boundaryId, data]) => {
                if (!data || !data.features) return;

                const regionsForBoundary = selectedRegions[boundaryId] || [];

                const featuresToShow =
                    regionsForBoundary.length > 0
                        ? data.features.filter((feature) => {
                              if (!feature.properties) return false;
                              const featureId =
                                  feature.properties.EMD_CD ||  // 법정동 코드
                                  feature.properties.BJCD ||
                                  feature.properties.ADM_CD ||
                                  feature.properties.SIDO_CD;
                              return regionsForBoundary.includes(featureId);
                          })
                        : [];

                if (featuresToShow.length > 0) {
                    const filteredGeoJson = {
                        type: "FeatureCollection",
                        features: featuresToShow,
                    };

                    mapInstance.data.addGeoJson(filteredGeoJson);
                }
            });

            mapInstance.data.setStyle((feature) => {
                const featureId =
                    feature.getProperty("EMD_CD") ||  // 법정동 코드
                    feature.getProperty("BJCD") ||
                    feature.getProperty("ADM_CD") ||
                    feature.getProperty("SIDO_CD");
                const isSelected = Object.values(selectedRegions)
                    .flat()
                    .includes(featureId);
                return {
                    fillColor: isSelected ? "#FF4500" : "#4CAF50",
                    fillOpacity: 0.3, // 경계를 더 투명하게 하여 heatmap과 marker가 위에 잘 보이도록 함
                    strokeColor: "#FFF",
                    strokeWeight: 1,
                };
            });

            const bounds = new window.google.maps.LatLngBounds();
            Object.entries(geoJsonData).forEach(([boundaryId, data]) => {
                if (!data || !data.features) return;
                const regionsForBoundary = selectedRegions[boundaryId] || [];
                const featuresToShow =
                    regionsForBoundary.length > 0
                        ? data.features.filter((feature) => {
                              if (!feature.properties) return false;
                              const featureId =
                                  feature.properties.EMD_CD ||  // 법정동 코드
                                  feature.properties.BJCD ||
                                  feature.properties.ADM_CD ||
                                  feature.properties.SIDO_CD;
                              return regionsForBoundary.includes(featureId);
                          })
                        : [];

                featuresToShow.forEach((feature) => {
                    const process = (coords) => {
                        if (coords[0] && Array.isArray(coords[0][0][0])) {
                            coords.forEach((subCoords) => process(subCoords));
                        } else {
                            coords.forEach((path) => {
                                path.forEach((p) =>
                                    bounds.extend(
                                        new window.google.maps.LatLng(p[1], p[0])
                                    )
                                );
                            });
                        }
                    };
                    if (feature.geometry) {
                        process(feature.geometry.coordinates);
                    }
                });
            });

            if (bounds.isEmpty() === false) {
                mapInstance.fitBounds(bounds);
            }
        }, 300); // 300ms 디바운스

        return () => clearTimeout(timer);
    }, [mapInstance, selectedRegions, geoJsonData]);

    useEffect(() => {
        // Google Maps API가 이미 로드되어 있는지 확인
        if (window.google && window.google.maps) {
            // 이미 로드되어 있으면 바로 지도 초기화
            initMap();
            return;
        }

        // 이미 스크립트 태그가 있는지 확인
        const existingScript = document.querySelector(
            `script[src*="maps.googleapis.com/maps/api/js"]`
        );
        
        if (existingScript) {
            // 스크립트가 이미 있으면 로드 완료를 기다림
            if (window.google && window.google.maps) {
                initMap();
            } else {
                existingScript.addEventListener('load', () => {
                    initMap();
                });
            }
            return;
        }

        // 스크립트가 없으면 새로 생성하여 추가
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places,marker,geometry,visualization`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
            initMap();
        };
        script.onerror = (e) => {
            console.error("Failed to load Google Maps script:", e);
        };
        document.head.appendChild(script);
    }, []);

    // CSV 파일을 파싱하는 함수 (따옴표로 감싼 필드 처리)
    const parseCSV = (csvText) => {
        const lines = csvText.split('\n').filter(line => line.trim());
        if (lines.length < 2) return [];
        
        const parseCSVLine = (line) => {
            const result = [];
            let current = '';
            let inQuotes = false;
            
            for (let i = 0; i < line.length; i++) {
                const char = line[i];
                
                if (char === '"') {
                    inQuotes = !inQuotes;
                } else if (char === ',' && !inQuotes) {
                    result.push(current.trim());
                    current = '';
                } else {
                    current += char;
                }
            }
            result.push(current.trim());
            return result;
        };
        
        const headers = parseCSVLine(lines[0]);
        const data = [];
        
        for (let i = 1; i < lines.length; i++) {
            const values = parseCSVLine(lines[i]);
            const row = {};
            headers.forEach((header, index) => {
                row[header] = values[index] || '';
            });
            
            // 위도, 경도, 데이터명이 있는 경우에만 추가
            if (row['위도'] && row['경도'] && row['데이터명']) {
                const lat = parseFloat(row['위도']);
                const lng = parseFloat(row['경도']);
                if (!isNaN(lat) && !isNaN(lng)) {
                    data.push({
                        name: row['데이터명'],
                        lat: lat,
                        lng: lng,
                        address: row['주소'] || '',
                        weight: row['데이터값'] ? parseFloat(row['데이터값']) : undefined
                    });
                }
            }
        }
        
        return data;
    };

    // newData 폴더의 모든 CSV 파일 로드 (버스정류장, 숙박시설 포함)
    useEffect(() => {
        setIsDataLoading(true);
        const loadNewData = async () => {
            // 파일 목록 JSON에서 파일 목록 가져오기
            let newDataFileList = [];
            try {
                const fileListResponse = await fetch(`${process.env.PUBLIC_URL}/mapData/newDataFileList.json`);
                if (fileListResponse.ok) {
                    newDataFileList = await fileListResponse.json();
                    console.log('newDataFileList:', newDataFileList);
                } else {
                    console.error('Failed to load newDataFileList.json');
                    setIsDataLoading(false);
                    return;
                }
            } catch (error) {
                console.error('Error loading newDataFileList.json:', error);
                setIsDataLoading(false);
                return;
            }

            const loadedData = {};
            const totalFiles = newDataFileList.length;
            
            setLoadingProgress({ current: 0, total: totalFiles, currentFile: '' });
            
            // 순차적으로 파일 로드 (한 번에 하나씩)
            for (let i = 0; i < newDataFileList.length; i++) {
                const file = newDataFileList[i];
                const fileName = file.replace('.csv', '');
                
                setLoadingProgress({ 
                    current: i + 1, 
                    total: totalFiles, 
                    currentFile: fileName 
                });
                
                try {
                    const response = await fetch(`${process.env.PUBLIC_URL}/mapData/newData/${encodeURIComponent(file)}`);
                    if (response.ok) {
                        const csvText = await response.text();
                        const parsedData = parseCSV(csvText);
                        if (parsedData.length > 0) {
                            const fileData = {};
                            
                            // 주소에서 도시명 추출하여 카테고리별로 분류 (17개 행정구역만 허용, 나머지는 기타)
                            // 정확한 행정구역 목록 (화이트리스트) - 표준 순서
                            const validRegions = [
                                '서울특별시',
                                '부산광역시',
                                '대구광역시',
                                '인천광역시',
                                '광주광역시',
                                '대전광역시',
                                '울산광역시',
                                '세종특별자치시',
                                '경기도',
                                '강원특별자치도',
                                '충청북도',
                                '충청남도',
                                '전북특별자치도',
                                '전라남도',
                                '경상북도',
                                '경상남도',
                                '제주특별자치도'
                            ];
                            
                            parsedData.forEach((item) => {
                                const address = item.address || '';
                                let cityName = '';
                                
                                // 특별자치도 먼저 체크 (제주특별자치도, 강원특별자치도, 전북특별자치도)
                                if (address.includes('특별자치도')) {
                                    const match = address.match(/([가-힣]+특별자치도)/);
                                    cityName = match ? match[1] : '';
                                } else if (address.includes('특별자치시')) {
                                    const match = address.match(/([가-힣]+특별자치시)/);
                                    cityName = match ? match[1] : '';
                                } else if (address.includes('광역시')) {
                                    const match = address.match(/([가-힣]+광역시)/);
                                    cityName = match ? match[1] : '';
                                } else if (address.includes('특별시')) {
                                    const match = address.match(/([가-힣]+특별시)/);
                                    cityName = match ? match[1] : '';
                                } else if (address.includes('도')) {
                                    // "도"가 포함된 경우 도 단위 추출
                                    const match = address.match(/([가-힣]+도)/);
                                    cityName = match ? match[1] : '';
                                }
                                
                                // 추출한 도시명이 유효한 행정구역 목록에 있는지 확인
                                if (cityName && validRegions.includes(cityName)) {
                                    if (!fileData[cityName]) {
                                        fileData[cityName] = [];
                                    }
                                    fileData[cityName].push(item);
                                } else {
                                    // 유효하지 않은 도시명이거나 추출하지 못한 경우 "기타"로 분류
                                    if (!fileData['기타']) {
                                        fileData['기타'] = [];
                                    }
                                    fileData['기타'].push(item);
                                }
                            });
                            
                            // 파일명에서 확장자 제거하여 키로 사용
                            loadedData[fileName] = fileData;
                            
                            // 각 파일 로드 후 즉시 상태 업데이트 (점진적 표시)
                            setNewDataFiles({ ...loadedData });
                        }
                    }else{
                        console.error(`Failed to load ${file}:`, response.statusText);
                    }
                } catch (error) {
                    console.error(`Failed to load ${file}:`, error);
                }
                
                // 다음 파일 로드 전 약간의 딜레이 (브라우저가 UI 업데이트할 시간 제공)
                await new Promise(resolve => setTimeout(resolve, 10));
            }
            
            console.log('Loaded newData files:', Object.keys(loadedData));
            setNewDataFiles(loadedData);
            setIsDataLoading(false);
            setLoadingProgress({ current: totalFiles, total: totalFiles, currentFile: '' });
        };

        loadNewData();
    }, []);

    const initMap = () => {
        if (!mapRef.current) {
            return;
        }
        const map = new window.google.maps.Map(mapRef.current, {
            center: { lat: 37.5165, lng: 126.9780 },    
            zoom: 13,
            mapId: "DEMO_MAP_ID",
            disableDefaultUI: true,
        });
        setMapInstance(map);
        // 초기 로딩은 시도 경계만 로드 (성능 최적화)
        // 나머지는 사용자가 요청할 때 로드
        const initialBoundary = boundaryOptions.find(opt => opt.id === "sido");
        if (initialBoundary) {
            renderPolygons(initialBoundary.url, initialBoundary.id);
        }
    };

    // Map copy.js의 공간정보 데이터 렌더링 로직 - 디바운싱으로 최적화
    useEffect(() => {
        if (!mapInstance) return;

        let zoomChangedTimer;
        let zoomChangedListener;
        
        // 줌 변경 이벤트 리스너 추가 (줌 레벨에 따라 클러스터링 재구성)
        zoomChangedListener = mapInstance.addListener('zoom_changed', () => {
            const currentZoom = mapInstance.getZoom();
            console.log('현재 줌 레벨:', currentZoom);
            
            // 줌 변경 시 데이터 재렌더링 (클러스터링 재구성)
            if (zoomChangedTimer) clearTimeout(zoomChangedTimer);
            zoomChangedTimer = setTimeout(() => {
                setAppliedSpatialDataState((prev) => ({ ...prev }));
            }, DEBOUNCE_CONFIG.ZOOM_CHANGED);
        });

        // 디바운스 타이머
        const timer = setTimeout(() => {
            // Clear previous data layers
            activeMarkerClusterers.forEach((clusterer) => clusterer.clearMarkers());
            activeHeatmaps.forEach((heatmap) => heatmap.setMap(null));
            setActiveMarkerClusterers([]);
            setActiveHeatmaps([]);

            const newMarkerClusterers = [];
            const newHeatmaps = [];

            // 적용된 상태(appliedSpatialDataState)를 기반으로 지도에 표시
            spatialInfoOptions.forEach((spatial) => {
                const state = appliedSpatialDataState[spatial.id];
                if (!state) return; // 상태가 없으면 스킵
                
                const selectedCategoryIds = Array.from(state.selected);
                
                if (selectedCategoryIds.length === 0) return;

                selectedCategoryIds.forEach((categoryId) => {
                    const category = spatial.categories.find(c => c.id === categoryId);
                    if (!category) return;
                    if (!category.data || category.data.length === 0) return; // 데이터가 없으면 스킵

                    if (state.mode === "POI") {
                        // POI 모드: 성능 최적화를 위한 데이터 필터링
                        const MAX_MARKERS = 10000; // 최대 마커 개수 제한
                        const bounds = mapInstance.getBounds();
                        let dataToRender = category.data;
                        
                        // 데이터가 너무 많으면 샘플링
                        if (dataToRender.length > MAX_MARKERS) {
                            // 현재 화면 영역 내 데이터 우선, 그 다음 샘플링
                            if (bounds) {
                                const inBounds = dataToRender.filter((item) => 
                                    bounds.contains({ lat: item.lat, lng: item.lng })
                                );
                                const outOfBounds = dataToRender.filter((item) => 
                                    !bounds.contains({ lat: item.lat, lng: item.lng })
                                );
                                
                                // 화면 내 데이터 + 화면 밖 데이터 샘플링
                                const sampleSize = Math.max(0, MAX_MARKERS - inBounds.length);
                                const sampled = outOfBounds.length > 0 
                                    ? outOfBounds.filter((_, i) => i % Math.ceil(outOfBounds.length / sampleSize) === 0)
                                    : [];
                                dataToRender = [...inBounds, ...sampled].slice(0, MAX_MARKERS);
                            } else {
                                // bounds가 없으면 균등 샘플링
                                const step = Math.ceil(dataToRender.length / MAX_MARKERS);
                                dataToRender = dataToRender.filter((_, i) => i % step === 0).slice(0, MAX_MARKERS);
                            }
                        } else if (bounds) {
                            // 화면 영역 내 데이터만 표시 (데이터가 많지 않아도 성능 향상)
                            dataToRender = dataToRender.filter((item) => 
                                bounds.contains({ lat: item.lat, lng: item.lng })
                            );
                        }
                        
                        // 마커 생성 (requestAnimationFrame을 사용한 청크 단위 렌더링)
                        const markers = dataToRender.map((item) => {
                            const markerContent = document.createElement("div");
                            markerContent.className = "custom-marker";
                            
                            // name과 weight 정보를 함께 표시 (weight가 유효한 숫자일 때만)
                            const displayText = item.weight !== undefined && 
                                                item.weight !== null && 
                                                !isNaN(item.weight) && 
                                                item.weight !== ''
                                ? `${item.name} (${item.weight})`
                                : item.name;
                            
                            markerContent.style.cssText = `
                                background-color:rgb(72, 148, 248); color: white; padding: 4px 8px;
                                border-radius: 15px; font-size: 12px; font-weight: bold;
                                white-space: nowrap; display: inline-block;
                                box-shadow: 2px 0 5px rgba(0 ,0 ,0,0.1);
                            `;
                            markerContent.textContent = displayText;

                            return new window.google.maps.marker.AdvancedMarkerElement({
                                position: { lat: item.lat, lng: item.lng },
                                content: markerContent,
                                zIndex: 1000, // 경계 레이어보다 위에 표시되도록 높은 z-index 설정
                            });
                        });

                        // 클러스터 스타일: 원형에 숫자만 표시
                        const renderer = {
                            render: (cluster, stats, map) => {
                                const count = cluster.count;
                                const position = cluster.position;
                                
                                // 원형 클러스터 아이콘 생성
                                const clusterDiv = document.createElement('div');
                                clusterDiv.style.cssText = `
                                    width: 40px;
                                    height: 40px;
                                    background-color: #00B7C7;
                                    border-radius: 50%;
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    color: #FFFFFF;
                                    font-size: 14px;
                                    font-weight: bold;
                                    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                                `;
                                clusterDiv.textContent = count.toString();
                                
                                return new window.google.maps.marker.AdvancedMarkerElement({
                                    position,
                                    content: clusterDiv,
                                    zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
                                });
                            },
                        };
                        
                        // 현재 줌 레벨 확인
                        const currentZoom = mapInstance.getZoom();
                        const CLUSTER_THRESHOLD_ZOOM = 15; // 이 줌 레벨 미만에서는 항상 클러스터링
                        
                        // 줌 레벨이 15보다 낮으면 모든 마커를 클러스터로 강제 표시
                        if (currentZoom < CLUSTER_THRESHOLD_ZOOM) {
                            // 데이터가 하나만 있어도 클러스터로 표시하기 위해 renderer에서 처리
                            const forceClusterRenderer = {
                                render: (cluster, stats, map) => {
                                    const count = cluster.count;
                                    const position = cluster.position;
                                    
                                    // 원형 클러스터 아이콘 생성
                                    const clusterDiv = document.createElement('div');
                                    clusterDiv.style.cssText = `
                                        width: 40px;
                                        height: 40px;
                                        background-color: #00B7C7;
                                        border-radius: 50%;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        color: #FFFFFF;
                                        font-size: 14px;
                                        font-weight: bold;
                                        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                                    `;
                                    clusterDiv.textContent = count.toString();
                                    
                                    return new window.google.maps.marker.AdvancedMarkerElement({
                                        position,
                                        content: clusterDiv,
                                        zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
                                    });
                                },
                            };
                            
                            const clusterer = new MarkerClusterer({
                                map: mapInstance,
                                markers: markers,
                                gridSize: dataToRender.length > 5000 ? 400 : dataToRender.length > 2000 ? 300 : 150,
                                maxZoom: 21, // 줌 레벨이 낮으면 항상 클러스터링 유지
                                algorithmOptions: {
                                    maxZoom: 21, // 모든 줌 레벨에서 클러스터링 강제
                                    radius: 1000, // 클러스터링 반경을 매우 크게 설정하여 단일 마커도 클러스터링되도록 함
                                },
                                renderer: forceClusterRenderer,
                            });
                            newMarkerClusterers.push(clusterer);
                        } else {
                            // 줌 레벨이 15 이상이면 일반 클러스터링
                            const clusterer = new MarkerClusterer({
                                map: mapInstance,
                                markers: markers,
                                gridSize: dataToRender.length > 5000 ? 400 : dataToRender.length > 2000 ? 300 : 150,
                                maxZoom: CLUSTER_THRESHOLD_ZOOM, // 줌 레벨이 높으면 개별 마커 표시
                                algorithmOptions: {
                                    maxZoom: CLUSTER_THRESHOLD_ZOOM,
                                    radius: dataToRender.length > 5000 ? 500 : 500,
                                },
                                renderer: renderer,
                            });
                            newMarkerClusterers.push(clusterer);
                        }
                    } else if (state.mode === "Heatmap") {
                        // Heatmap 모드: 성능 최적화를 위한 데이터 샘플링
                        const MAX_HEATMAP_POINTS = 20000; // 히트맵 최대 포인트 수
                        const bounds = mapInstance.getBounds();
                        let dataToRender = category.data;
                        
                        // 데이터가 너무 많으면 샘플링
                        if (dataToRender.length > MAX_HEATMAP_POINTS) {
                            if (bounds) {
                                // 화면 영역 내 데이터 우선
                                const inBounds = dataToRender.filter((item) => 
                                    bounds.contains({ lat: item.lat, lng: item.lng })
                                );
                                const outOfBounds = dataToRender.filter((item) => 
                                    !bounds.contains({ lat: item.lat, lng: item.lng })
                                );
                                
                                const sampleSize = Math.max(0, MAX_HEATMAP_POINTS - inBounds.length);
                                const sampled = outOfBounds.length > 0 
                                    ? outOfBounds.filter((_, i) => i % Math.ceil(outOfBounds.length / sampleSize) === 0)
                                    : [];
                                dataToRender = [...inBounds, ...sampled].slice(0, MAX_HEATMAP_POINTS);
                            } else {
                                const step = Math.ceil(dataToRender.length / MAX_HEATMAP_POINTS);
                                dataToRender = dataToRender.filter((_, i) => i % step === 0).slice(0, MAX_HEATMAP_POINTS);
                            }
                        } else if (bounds) {
                            // 화면 영역 내 데이터만 표시
                            dataToRender = dataToRender.filter((item) => 
                                bounds.contains({ lat: item.lat, lng: item.lng })
                            );
                        }
                        
                        const heatmapData = dataToRender.map((p) => ({
                            location: new window.google.maps.LatLng(p.lat, p.lng),
                            weight: p.weight || 50,
                        }));
                        const newHeatmap =
                            new window.google.maps.visualization.HeatmapLayer({
                                data: heatmapData,
                                map: mapInstance,
                                radius: spatial.radius || 20,
                                // HeatmapLayer는 자동으로 DataLayer보다 위에 렌더링됨
                                // 명시적으로 opacity를 설정하여 경계 위에 잘 보이도록 함
                            });
                        newHeatmap.set("opacity", 0.7); // 경계 위에 잘 보이도록 opacity 조정
                        newHeatmaps.push(newHeatmap);
                    }
                });
            });

            setActiveMarkerClusterers(newMarkerClusterers);
            setActiveHeatmaps(newHeatmaps);
        }, DEBOUNCE_CONFIG.DATA_RENDERING); // 데이터 렌더링 디바운스 (성능 최적화)

        return () => {
            clearTimeout(timer);
            if (zoomChangedTimer) clearTimeout(zoomChangedTimer);
            if (zoomChangedListener) {
                window.google.maps.event.removeListener(zoomChangedListener);
            }
        };
    }, [mapInstance, appliedSpatialDataState]);

    // 지도 bounds 변경 시 데이터 재렌더링 (화면 영역 내 데이터만 표시)
    useEffect(() => {
        if (!mapInstance) return;
        
        let boundsChangedTimer;
        const boundsChangedListener = mapInstance.addListener('bounds_changed', () => {
            // bounds 변경 시 데이터 재렌더링 트리거 (debounce 포함)
            if (boundsChangedTimer) clearTimeout(boundsChangedTimer);
            boundsChangedTimer = setTimeout(() => {
                // appliedSpatialDataState를 강제로 업데이트하여 재렌더링
                setAppliedSpatialDataState((prev) => ({ ...prev }));
            }, DEBOUNCE_CONFIG.BOUNDS_CHANGED);
        });
        
        return () => {
            if (boundsChangedTimer) clearTimeout(boundsChangedTimer);
            if (boundsChangedListener) {
                window.google.maps.event.removeListener(boundsChangedListener);
            }
        };
    }, [mapInstance]);

    const handleSearch = async (keyword, mode = "default") => {
        if (!keyword || !mapInstance) return;



        try {
            // 검색 시작 시 경로 관련 상태 초기화
            if (mode === "start" || mode === "end") {
                setRouteData(null);
                setRouteDistance(null);
                setRouteDuration(null);
                setShowDetails(false);

                if (routePolyline) {
                    routePolyline.forEach((line) => line.setMap(null));
                    setRoutePolyline(null);
                }

                activeLines.forEach((line) => line.setMap(null));
                setActiveLines([]);
                setTransitSteps([]);
                setCarSteps([]);
                setWalkSteps([]);
            }

            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_SERVER}/map/search-with-geo`,
                {
                    params: { keyword },
                }
            );



            if (mode === "default") {
                setSearchResults(response.data);
                activeMarkers.forEach((m) => m.setMap(null));
                setActiveMarkers([]);

                if (response.data.length > 0) {
                    const { lat, lng } = response.data[0];
                    moveTo(lat, lng);
                }
            } else {
                setDirectionMode(mode);
                setDirectionResults(response.data);

            }
        } catch (error) {
            console.error("검색 실패:", error);
            alert(`${content?.mapSearchError || "검색에 실패했습니다"}: ${error.response?.data?.message || error.message}`);
        }
    };

    const moveTo = (lat, lng) => {
        if (!mapInstance) return;

        const position = { lat, lng };
        mapInstance.setCenter(position);

        activeMarkers.forEach((m) => m.setMap(null));
        setActiveMarkers([]);

        const pin = new window.google.maps.marker.PinElement({
            background: "#F84848",
            borderColor: "#F84848",
            glyphColor: "white",
        });
        pin.element.style.filter = "drop-shadow(0px 5px 5px rgba(0,0,0,.35))";

        const newMarker = new window.google.maps.marker.AdvancedMarkerElement({
            position,
            map: mapInstance,
            content: pin.element,
        });

        setActiveMarkers([newMarker]);
    };

    // 길찾기 관련 함수들
    const createDirectionMarker = (lat, lng, type) => {
        const isStart = type === "출발";
        const url = isStart ? "/img/marker_start.svg" : "/img/marker_end.svg";

        const icon = {
            url,
            scaledSize: new window.google.maps.Size(40, 48),
            anchor: new window.google.maps.Point(20, 48),
        };

        return new window.google.maps.Marker({
            position: { lat, lng },
            map: mapInstance,
            icon,
            title: type,
            zIndex: isStart ? 2 : 1,
        });
    };

    const transformSteps = (steps) => {
        const result = [];

        steps.forEach((step) => {
            if (step.travel_mode === "TRANSIT" && step.transit_details) {
                const lineName =
                    step.transit_details.line_short_name ||
                    step.transit_details.line_name ||
                    "";
                const vehicle =
                    step.transit_details.vehicle_type === "SUBWAY"
                        ? "지하철"
                        : "버스";
                const departure = step.transit_details.departure_stop;
                const arrival = step.transit_details.arrival_stop;
                const stops = step.transit_details.num_stops;

                // 승차
                result.push({
                    type: "board",
                    text: `${lineName} ${vehicle} ${departure} 에서 승차`,
                });

                // 이동
                result.push({
                    type: "move",
                    text: `${stops} 정거장 이동`,
                });

                // 하차
                result.push({
                    type: "alight",
                    text: `${lineName} ${vehicle} ${arrival} 에서 하차`,
                });
            } else {
                // WALKING은 그대로
                result.push(step);
            }
        });

        return result;
    };

    // 짝수 인덱스 스텝에 다음 홀수 인덱스의 distance/duration를 병합
    const mergeTmapCarSteps = (steps = []) => {
        const merged = [];
        let i = 0;

        while (i < steps.length) {
            const a = steps[i]; // 메인(보통 짝수)
            const b = steps[i + 1]; // 거리/시간(보통 홀수)

            // a가 없으면 종료
            if (!a) break;

            // 병합 객체 사본
            const m = { ...a };

            // b가 존재하고, turnType이 비어있고, distance/duration 정보를 갖고 있으면 병합
            const bHasMetric = b && (b.distance != null || b.duration != null);
            const bLooksLikeMetricOnly =
                b && (b.turnType == null || b.turnType === undefined);

            if (bHasMetric && bLooksLikeMetricOnly) {
                if (m.distance == null && b.distance != null)
                    m.distance = b.distance;
                if (m.duration == null && b.duration != null)
                    m.duration = b.duration;
                i += 2; // a,b 둘 다 소비
            } else {
                i += 1; // a만 소비
            }

            merged.push(m);
        }

        return merged;
    };



    const handleDirectionSelect = (item) => {

        
        const cleanTitle = item.title.replace(/<[^>]*>/g, "");
        const { lat, lng } = item;
        const label = directionMode === "start" ? "출발" : "도착";



        if (!mapInstance) {
            console.error("mapInstance가 없습니다!");
            return;
        }

        const marker = createDirectionMarker(lat, lng, label);
        mapInstance.setCenter({ lat, lng });

        if (directionMode === "start") {
            if (startMarker) startMarker.setMap(null);
            setStartMarker(marker);
            setStartInfo({ lat, lng, title: cleanTitle });
            setStartAddress(cleanTitle);
    
        } else {
            if (endMarker) endMarker.setMap(null);
            setEndMarker(marker);
            setEndInfo({ lat, lng, title: cleanTitle });
            setEndAddress(cleanTitle);
    
        }

        setDirectionMode(null);
        setDirectionResults([]);
    };

    const setAsStart = (item) => {
        activeMarkers.forEach((m) => m.setMap(null));
        setActiveMarkers([]);

        if (!mapInstance) return;
        const title = item.title.replace(/<[^>]*>/g, "");

        if (startMarker) startMarker.setMap(null);
        const m = createDirectionMarker(item.lat, item.lng, "출발");
        setStartMarker(m);
        setStartInfo({ lat: item.lat, lng: item.lng, title });
        setStartAddress(title);

        mapInstance.setCenter({ lat: item.lat, lng: item.lng });

        setInfoIndex(1);
        if (isMobile) {
            setMobileStage("directions");
            setIsPanelOpen(true);
        }

        if (endInfo) requestRoute(travelMode);
    };

    const setAsEnd = (item) => {
        activeMarkers.forEach((m) => m.setMap(null));
        setActiveMarkers([]);

        if (!mapInstance) return;
        const title = item.title.replace(/<[^>]*>/g, "");

        if (endMarker) endMarker.setMap(null);
        const m = createDirectionMarker(item.lat, item.lng, "도착");
        setEndMarker(m);
        setEndInfo({ lat: item.lat, lng: item.lng, title });
        setEndAddress(title);

        mapInstance.setCenter({ lat: item.lat, lng: item.lng });

        setInfoIndex(1);
        if (isMobile) {
            setMobileStage("directions");
            setIsPanelOpen(true);
        }

        if (startInfo) requestRoute(travelMode);
    };

    const handleReverse = () => {
        setStartAddress(endAddress);
        setEndAddress(startAddress);

        const si = startInfo;
        const ei = endInfo;
        setStartInfo(ei ?? null);
        setEndInfo(si ?? null);

        if (startMarker) startMarker.setMap(null);
        if (endMarker) endMarker.setMap(null);



        if (ei) setStartMarker(createDirectionMarker(ei.lat, ei.lng, "출발"));
        if (si) setEndMarker(createDirectionMarker(si.lat, si.lng, "도착"));
    };



    const resetDirections = () => {
        const off = (x) => {
            if (!x) return;
            if (Array.isArray(x)) x.forEach(off);
            else if (typeof x.setMap === "function") x.setMap(null);
        };

        off(startMarker);
        off(endMarker);
        off(routePolyline);
        off(activeLines);
        off(activeMarkers);

        setStartInfo(null);
        setEndInfo(null);
        setStartAddress("");
        setEndAddress("");
        setDirectionMode(null);
        setDirectionResults([]);
        setRouteData(null);
        setRouteDistance(null);
        setRouteDuration(null);
        setTransitSteps([]);
        setCarSteps([]);
        setWalkSteps([]);
        setShowDetails(false);
    };

    // 탭 클릭 시 기존 결과 정리하는 함수
    const handleTabChange = (index, stageForMobile = null) => {
        // 탭 변경 전에 기존 결과 정리
        resetDirections();

        // 탭 변경
        setInfoIndex(index);

        if (isMobile && stageForMobile) {
            setMobileStage(stageForMobile);
            setIsPanelOpen(true);
        }
    };

    // 슬라이드 버튼
    const groupRef = useRef(null);
    const tabRefs = useRef({}); // { car: spanEl, walk: spanEl, transit: spanEl }

    const placeIndicatorWhenReady = (tries = 12) => {
        const group = groupRef.current;
        const key = travelMode || "car";
        const el = tabRefs.current[key];
        if (!group || !el) return;

        const visible = group.offsetWidth > 0 && el.offsetWidth > 0;
        if (!visible) {
            if (tries > 0)
                setTimeout(() => placeIndicatorWhenReady(tries - 1), 50);
            return;
        }

        const gr = group.getBoundingClientRect();
        const er = el.getBoundingClientRect();
        const pad = parseFloat(getComputedStyle(group).paddingLeft) || 0;

        group.style.setProperty("--ind-x", `${er.left - gr.left - pad}px`);
        group.style.setProperty("--ind-w", `${er.width}px`);
    };

    // 최초
    useLayoutEffect(() => {
        requestAnimationFrame(() => placeIndicatorWhenReady());
    }, []);

    // 모드 변경 시
    useLayoutEffect(() => {
        placeIndicatorWhenReady();
    }, [travelMode]);

    // 패널 열릴 때/리사이즈 시
    useEffect(() => {
        const onResize = () => placeIndicatorWhenReady();
        window.addEventListener("resize", onResize);
        if (isPanelOpen) requestAnimationFrame(() => placeIndicatorWhenReady());
        return () => window.removeEventListener("resize", onResize);
    }, [isPanelOpen]);

    // 폰트 로드 후
    useEffect(() => {
        if (document.fonts?.ready)
            document.fonts.ready.then(() => placeIndicatorWhenReady());
    }, []);

    // **중요: 길찾기 탭이 다시 보일 때 재배치**
    useEffect(() => {
        const isDirectionsTab = infoIndex === 1; // 길찾기 탭 인덱스
        if (isDirectionsTab && isPanelOpen) placeIndicatorWhenReady();
    }, [infoIndex, isPanelOpen]);

    // 레이아웃 변동 요인 추가(모바일/도크)
    useEffect(() => {
        placeIndicatorWhenReady();
    }, [isMobile, dockOpen]);

    const requestRoute = async (mode) => {
        if (!startInfo || !endInfo) {
            alert(content?.mapSelectStartEnd || "출발지와 도착지를 모두 입력해주세요.");
            return;
        }

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_SERVER}/map/directions`,
                {
                    start: { lat: startInfo.lat, lng: startInfo.lng },
                    end: { lat: endInfo.lat, lng: endInfo.lng },
                    mode: mode,
                }
            );



            const routes = response.data;
            if (routes.length > 0) {
                const route = routes[0];

                
                // 경로 데이터 설정
                setRouteData(route);
                
                // 거리와 시간 설정
                if (route.distance) {
                    setRouteDistance(route.distance);
                }
                if (route.duration) {
                    setRouteDuration(route.duration);
                }
                
                // 경로 단계 설정
                if (route.steps) {
                    if (mode === "car") {
                        setCarSteps(route.steps);
                    } else if (mode === "walk") {
                        setWalkSteps(route.steps);
                    } else if (mode === "transit") {
                        setTransitSteps(route.steps);
                    }
                }
                
                setShowDetails(true); // 경로가 성공하면 상세정보 보이기
                if (isMobile) {
                    setIsPanelOpen(false);
                }
            } else {
                setRouteData(null);
                setRouteDistance(null);
                setRouteDuration(null);
                setCarSteps([]);
                setWalkSteps([]);
                setTransitSteps([]);
                alert(content?.mapRouteNotFound || "경로를 찾을 수 없습니다.");
            }
        } catch (err) {
            console.error("경로 요청 실패:", err);
            console.error("에러 세부사항:", err.response?.data);
            alert(`${content?.mapRouteError || "경로를 찾는 데 실패했습니다"}: ${err.response?.data?.message || err.message}`);
        }
    };

    // 1) turnType → 아이콘/라벨 매핑
    const TURN_META = {
        200: { icon: "/img/icon_map_start.png", label: "출발" },
        201: { icon: "/img/icon_map_start.png", label: "도착" },

        12: { icon: "/img/icon_map_turn_left.png", label: "좌회전" },
        13: { icon: "/img/icon_map_turn_right.png", label: "우회전" },
        14: { icon: "/img/icon_map_u_turn.png", label: "유턴" },

        // 확장 매핑(선택): 문서/테스트 케이스에 맞게 조정
        16: { icon: "/img/icon_map_u_turn.png", label: "P턴" },
        17: { icon: "/img/icon_map_turn_left.png", label: "좌측 급회전" },
        18: { icon: "/img/icon_map_turn_right.png", label: "우측 급회전" },
        19: { icon: "/img/icon_map_turn_left.png", label: "좌측 진입" },
        20: { icon: "/img/icon_map_turn_right.png", label: "우측 진입" },
        21: { icon: "/img/icon_map_turn_left.png", label: "좌측 합류" },
        22: { icon: "/img/icon_map_turn_right.png", label: "우측 합류" },
        211: { icon: "/img/icon_map_via.png", label: "경유지 도착" },
        212: { icon: "/img/icon_map_via.png", label: "경유지 출발" },
    };
    const getTurnMeta = (code) =>
        TURN_META[code] ?? {
            icon: "/img/icon_map_straight.png",
            label: "안내",
        };



   // 렌더링 헬퍼 함수
   const renderStep = (step, index) => {
    if (step.travel_mode === "WALKING") {
        return (
            <li key={index} className="transit-detailed-route-walking">
                <img src="/img/icon_map_walk.png" alt="도보" />
                <div className="transit-detailed-route-info">
                    <p
                        dangerouslySetInnerHTML={{
                            __html: step.html_instructions,
                        }}
                    />
                    <span>
                        ({(step.distance / 1000).toFixed(2)}km,{" "}
                        {Math.round(step.duration / 60)}분)
                    </span>
                </div>
            </li>
        );
    }

    if (["board", "move", "alight"].includes(step.type)) {
        const isSubway = step.vehicle === "지하철";
        let iconMap = {};
        if (isSubway) {
            iconMap.move = null;
            iconMap.board = "/img/icon_map_subway.png";
            iconMap.alight = "/img/icon_map_subway.png";
        } else {
            iconMap.move = null;
            iconMap.board = "/img/icon_map_bus.png";
            iconMap.alight = "/img/icon_map_bus.png";
        }

        return (
            <li
                key={index}
                className={`transit-detailed-route-subway ${
                    step.type === "move" ? "move" : ""
                } ${
                    step.type === "move" && isSubway ? "move-subway" : ""
                } ${step.type === "move" && !isSubway ? "move-bus" : ""}`}
            >
                <div className="public-transportation-info">
                    {iconMap[step.type] && (
                        <img src={iconMap[step.type]} alt="대중교통" />
                    )}
                    <div className="public-transportation-text">
                        <div className="public-transportation-result">
                            <span>{step.text}</span>
                        </div>
                        {step.distance && (
                            <span>
                                ({(step.distance / 1000).toFixed(2)}km,{" "}
                                {Math.round(step.duration / 60)}분)
                            </span>
                        )}
                    </div>
                </div>
            </li>
        );
    }

    if (step.travel_mode === "TRANSIT") {
        const isSubway = step.transit_details?.vehicle_type === "SUBWAY";
        return (
            <li key={index} className="transit-detailed-route">
                <div className="public-transportation-info">
                    <img
                        src={
                            isSubway
                                ? "/img/icon_map_subway.png"
                                : "/img/icon_map_bus.png"
                        }
                        alt={isSubway ? "지하철" : "버스"}
                    />
                    <div className="public-transportation-text">
                        <div>
                            <p>
                                {step.transit_details?.line_short_name ||
                                    step.transit_details?.line_name ||
                                    "대중교통"}
                            </p>
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: step.html_instructions,
                                }}
                            />
                        </div>
                        <span>
                            ({(step.distance / 1000).toFixed(2)}km,{" "}
                            {Math.round(step.duration / 60)}분)
                        </span>
                    </div>
                </div>
            </li>
        );
    }

    return null;
};

    const renderPolygons = async (dataUrl, boundaryId) => {
        try {
            const response = await axios.get(dataUrl);
            const topoData = response.data;

            const objectKey = Object.keys(topoData.objects)[0];
            const features = topoData.objects[objectKey].geometries
                .map((geometry) =>
                    geometry ? topojson.feature(topoData, geometry) : null
                )
                .filter(Boolean);

            const geoData = { type: "FeatureCollection", features };
            setGeoJsonData((prevData) => ({
                ...prevData,
                [boundaryId]: geoData,
            }));
        } catch (error) {
            console.error("Error loading polygon data:", error);
        }
    };

    return (
        <div className="MapPage">
            <div className={`infoCon ${isPanelOpen ? "open" : "closed"}`}>
                <div className="menu">
                    {infoMenuList.map((item, index) => (
                        <div
                            key={index}
                            className={`tab tab-${item.id} ${
                                infoIndex === index ? "active" : ""
                            }`}
                            onClick={() => {
                                setInfoIndex(index);
                                if (isMobile) {
                                    if (index === 0) setMobileStage("layers");
                                    else if (index === 1) setMobileStage("directions");
                                    else setMobileStage("search");
                                }
                                setIsPanelOpen(true);
                            }}
                        >
                            {item.title}
                        </div>
                    ))}
                </div>
                <div className="info">
                    {/* 공간정보 탭 (index 0) */}
                    {infoIndex === 0 && (
                        <div className="informationWrap">
                            <div className="searchinputWrap">
                                {isMobile && isPanelOpen && (
                                    <button
                                        className="panelBackBtn"
                                        onClick={() => {
                                            setMobileStage("home");
                                            setIsPanelOpen(false);
                                        }}
                                    >
                                        ‹
                                    </button>
                                )}
                                <div className="searchInput">
                                    <input
                                        type="text"
                                        value={activeTab === (content?.mapBoundaryTab || "행정경계") ? boundarySearchKeyword : dataSearchKeyword}
                                        onChange={(e) => {
                                            if (activeTab === (content?.mapBoundaryTab || "행정경계")) {
                                                setBoundarySearchKeyword(e.target.value);
                                                searchBoundaries(e.target.value);
                                            } else {
                                                setDataSearchKeyword(e.target.value);
                                                searchData(e.target.value);
                                            }
                                        }}
                                        placeholder={content?.mapKeywordSearch || "키워드 검색"}
                                    />
                                    <button onClick={() => {
                                        if (activeTab === (content?.mapBoundaryTab || "행정경계")) {
                                            searchBoundaries(boundarySearchKeyword);
                                        } else {
                                            searchData(dataSearchKeyword);
                                        }
                                    }}>
                                        <img src="/img/icon_map_search.png" />
                                    </button>
                                </div>
                            </div>

                            <div className="informationPanel">
                                <div className="informationTabs">
                                    {[content?.mapBoundaryTab || "행정경계", content?.mapDataTab || "데이터"].map((t) => (
                                        <button
                                            key={t}
                                            className={`informationtab ${
                                                activeTab === t ? "active" : ""
                                            }`}
                                            onClick={() => {
                                                setActiveTab(t);
                                                // 탭 변경 시 검색 키워드 초기화
                                                if (t === (content?.mapBoundaryTab || "행정경계")) {
                                                    setDataSearchKeyword("");
                                                    setFilteredSpatialOptions([]);
                                                } else {
                                                    setBoundarySearchKeyword("");
                                                    setFilteredBoundaries({});
                                                }
                                            }}
                                        >
                                            {t}
                                        </button>
                                    ))}
                                </div>

                                {activeTab === (content?.mapBoundaryTab || "행정경계") && (
                                    <div className="tabContent">
                                        {boundaryOptions.map((boundary) => (
                                            <div key={boundary.id} className="section">
                                                <div
                                                    className="sectionHeader"
                                                    onClick={() => {
                                                        const isExpanding = !expandedBoundaries.includes(boundary.id);
                                                        setExpandedBoundaries(
                                                            (prev) =>
                                                                prev.includes(boundary.id)
                                                                    ? prev.filter((id) => id !== boundary.id)
                                                                    : [...prev, boundary.id]
                                                        );
                                                        // 펼칠 때만 데이터 로드 (성능 최적화)
                                                        if (isExpanding && !geoJsonData[boundary.id]) {
                                                            renderPolygons(boundary.url, boundary.id);
                                                        }
                                                    }}
                                                >
                                                    <span className="sectionHeaderText">
                                                        {boundary.name}
                                                    </span>
                                                    <span
                                                        className={`chev ${
                                                            expandedBoundaries.includes(boundary.id)
                                                                ? "open"
                                                                : ""
                                                        }`}
                                                    >
                                                        ▾
                                                    </span>
                                                </div>
                                                {expandedBoundaries.includes(boundary.id) && (
                                                    <div className="informationgGrid">
                                                        {((boundarySearchKeyword ? filteredBoundaries[boundary.id] : regionList[boundary.id]) || []).map((regionObject) => {
                                                            const key = `boundary:${boundary.id}:${regionObject.id}`;
                                                            const isSearchResult = boundarySearchKeyword && 
                                                                regionObject.name.toLowerCase().includes(boundarySearchKeyword.toLowerCase());
                                                            
                                                            return (
                                                                <label
                                                                    key={regionObject.id}
                                                                    className={`informationCheck ${isSearchResult ? 'searchResult' : ''}`}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isChecked(key)}
                                                                        onChange={(e) =>
                                                                            setMany([key], e.target.checked)
                                                                        }
                                                                    />
                                                                    <span>{regionObject.name}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {activeTab === (content?.mapDataTab || "데이터") && (
                                    <div className="tabContent">
                                        {(dataSearchKeyword ? filteredSpatialOptions : spatialInfoOptions).map((spatial) => {
                                                const st = spatialDataState[spatial.id] || { 
                                                    mode: spatial.type === "marker" ? "POI" : "Heatmap", 
                                                    selected: new Set() 
                                                };
                                                
                                                return (
                                                <div key={spatial.id} className="section">
                                                    <div
                                                        className="sectionHeader"
                                                        onClick={() => toggleOpenSpatialData(spatial.id)}
                                                    >
                                                        <div className="sectionHeaderText">
                                                            <div className="dataHeaderText">
                                                                <span>{spatial.name}</span>
                                                                <div className="selectmap">
                                                                    <label className="radio">
                                                                        <input
                                                                            type="radio"
                                                                            name={`map-${spatial.id}`}
                                                                            checked={st.mode === "POI"}
                                                                            onChange={() =>
                                                                                setSpatialMode(spatial.id, "POI")
                                                                            }
                                                                        />
                                                                        <span>POI</span>
                                                                    </label>
                                                                    <label className="radio">
                                                                        <input
                                                                            type="radio"
                                                                            name={`map-${spatial.id}`}
                                                                            checked={st.mode === "Heatmap"}
                                                                            onChange={() =>
                                                                                setSpatialMode(spatial.id, "Heatmap")
                                                                            }
                                                                        />
                                                                        <span>Heatmap</span>
                                                                    </label>
                                                                </div>
                                                            </div>
                                                            <span
                                                                className={`chev ${
                                                                    openSpatialData.has(spatial.id)
                                                                        ? "open"
                                                                        : ""
                                                                }`}
                                                            >
                                                                ▾
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {openSpatialData.has(spatial.id) && (
                                                        <div className="dataBody">
                                                            <div className="informationgGrid">
                                                                {/* 전체 선택 */}
                                                                {spatial.categories && spatial.categories.length > 0 && (
                                                                    <label className="informationCheck">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={spatialAllState(spatial.id).all}
                                                                            ref={(el) =>
                                                                                el &&
                                                                                (el.indeterminate =
                                                                                    spatialAllState(spatial.id).some)
                                                                            }
                                                                            onChange={(e) =>
                                                                                toggleAllSpatialCategories(
                                                                                    spatial.id,
                                                                                    e.target.checked
                                                                                )
                                                                            }
                                                                        />
                                                                        <span>{content?.mapSpatialDataAll || "전체"}</span>
                                                                    </label>
                                                                )}

                                                                {/* 개별 카테고리들 */}
                                                                {spatial.categories.map((category) => (
                                                                    <label
                                                                        key={category.id}
                                                                        className="informationCheck"
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={st.selected.has(category.id)}
                                                                            onChange={(e) =>
                                                                                toggleSpatialCategory(
                                                                                    spatial.id,
                                                                                    category.id,
                                                                                    e.target.checked
                                                                                )
                                                                            }
                                                                        />
                                                                        <span>{category.name}</span>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {isDataLoading && (
                                            <div style={{ padding: '20px', textAlign: 'center', borderTop: '1px solid #e0e0e0', marginTop: '10px' }}>
                                                <div style={{ fontSize: '14px', color: '#666' }}>
                                                    데이터 로딩중...
                                                    {loadingProgress.total > 0 && (
                                                        <span style={{ marginLeft: '10px' }}>
                                                            ({loadingProgress.current} / {loadingProgress.total})
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* 하단 버튼 */}
                            <div className="informationFooter">
                                <button 
                                    className="clearBtn" 
                                    onClick={() => {
                                        clearAll();
                                        // 공간정보 데이터도 모두 초기화
                                        setSpatialDataState(() =>
                                            Object.fromEntries(
                                                spatialInfoOptions.map((s) => [
                                                    s.id,
                                                    { mode: s.type === "marker" ? "POI" : "Heatmap", selected: new Set() },
                                                ])
                                            )
                                        );
                                        // 적용된 상태도 초기화
                                        setAppliedSpatialDataState(() =>
                                            Object.fromEntries(
                                                spatialInfoOptions.map((s) => [
                                                    s.id,
                                                    { mode: s.type === "marker" ? "POI" : "Heatmap", selected: new Set() },
                                                ])
                                            )
                                        );
                                    }}
                                >
                                    <img src="/img/icon_map_clearbtn.png" alt="clear" />
                                    <span>{content?.mapClearAll || "전체 해제"}</span>
                                </button>
                                <button 
                                    className="applyBtn"
                                    onClick={() => {
                                        // 경계 데이터 적용 로직 - Map copy.js 방식
                                        const boundarySelections = {};
                                        
                                        // checked에서 boundary: 키를 가진 항목들을 찾아서 selectedRegions에 반영
                                        checked.forEach(key => {
                                            if (key.startsWith('boundary:')) {
                                                const [, boundaryId, regionId] = key.split(':');
                                                if (!boundarySelections[boundaryId]) {
                                                    boundarySelections[boundaryId] = [];
                                                }
                                                boundarySelections[boundaryId].push(regionId);
                                            }
                                        });
                                        
                                        setSelectedRegions(boundarySelections);
                                        
                                        // 공간정보 데이터 적용 - spatialDataState를 appliedSpatialDataState로 복사
                                        setAppliedSpatialDataState({ ...spatialDataState });
                                        
                                        if (isMobile) {
                                            setMobileStage("home");
                                            setIsPanelOpen(false);
                                        }
                                    }}
                                >
                                    <span>{content?.mapApply || "적용하기"}</span>
                                    <img src="/img/icon_map_applybtn.png" alt="apply" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 길찾기 */}
                    {infoIndex === 1 && (
                        <div>
                            <div className="directionsSearchWrap">
                                <div className="directionsSearchTop">
                                    {isMobile && isPanelOpen && (
                                        <button
                                            className="panelBackBtn"
                                            style={{ paddingTop: "9px" }}
                                            onClick={() => {
                                                setMobileStage("home");
                                                setIsPanelOpen(false);
                                            }}
                                        >
                                            ‹
                                        </button>
                                    )}
                                    <div className="directionsSearch">
                                        <div className="originWrap">
                                            <button
                                                onClick={() =>
                                                    handleSearch(
                                                        startAddress,
                                                        "start"
                                                    )
                                                }
                                            >
                                                <img
                                                    src="/img/icon_map_origin.png"
                                                    alt="originicon"
                                                />
                                            </button>
                                            <input
                                                type="text"
                                                value={startAddress}
                                                onChange={(e) =>
                                                    setStartAddress(
                                                        e.target.value
                                                    )
                                                }
                                                placeholder={content?.mapStartPlaceholder || "출발지 입력"}
                                                onKeyDown={(e) =>
                                                    e.key === "Enter" &&
                                                    handleSearch(
                                                        startAddress,
                                                        "start"
                                                    )
                                                }
                                            />
                                        </div>
                                        <div className="searchBoxLine"></div>
                                        <div className="destinationWrap">
                                            <button
                                                onClick={() =>
                                                    handleSearch(
                                                        endAddress,
                                                        "end"
                                                    )
                                                }
                                            >
                                                <img
                                                    src="/img/icon_map_destination.png"
                                                    alt="destinationicon"
                                                />
                                            </button>
                                            <input
                                                type="text"
                                                value={endAddress}
                                                onChange={(e) =>
                                                    setEndAddress(
                                                        e.target.value
                                                    )
                                                }
                                                placeholder={content?.mapEndPlaceholder || "도착지 입력"}
                                                onKeyDown={(e) =>
                                                    e.key === "Enter" &&
                                                    handleSearch(
                                                        endAddress,
                                                        "end"
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className="destinationiconBox">
                                        <button
                                            className="reverseBtn"
                                            onClick={handleReverse}
                                        >
                                            <img
                                                src="/img/icon_map_reverse.png"
                                                alt="reverseicon"
                                            />
                                        </button>

                                        <button
                                            className="resetBtn"
                                            onClick={resetDirections}
                                        >
                                            <img
                                                src="/img/icon_map_reset.png"
                                                alt="reseticon"
                                            />
                                        </button>
                                    </div>
                                </div>
                                <div className="directionsSearchBottom">
                                    {/* <div className="modeGroup" ref={groupRef}>
                                        <span
                                            className="indicator"
                                            aria-hidden="true"
                                        />
                                        <label className="mode">
                                            <input
                                                type="radio"
                                                name="travel"
                                                value="car"
                                                checked={travelMode === "car"}
                                                onChange={(e) => {
                                                    setTravelMode(
                                                        e.target.value
                                                    );
                                                    setRouteData(null);
                                                }}
                                            />
                                            <span
                                                ref={(el) =>
                                                    (tabRefs.current.car = el)
                                                }
                                            >
                                                자동차
                                            </span>
                                        </label>
                                        <label className="mode">
                                            <input
                                                type="radio"
                                                name="travel"
                                                value="walk"
                                                checked={travelMode === "walk"}
                                                onChange={(e) => {
                                                    setTravelMode(
                                                        e.target.value
                                                    );
                                                    setRouteData(null);
                                                }}
                                            />
                                            <span
                                                ref={(el) =>
                                                    (tabRefs.current.walk = el)
                                                }
                                            >
                                                도보
                                            </span>
                                        </label>
                                        <label className="mode">
                                            <input
                                                type="radio"
                                                name="travel"
                                                value="transit"
                                                checked={
                                                    travelMode === "transit"
                                                }
                                                onChange={(e) => {
                                                    setTravelMode(
                                                        e.target.value
                                                    );
                                                    setRouteData(null);
                                                }}
                                            />
                                            <span
                                                ref={(el) =>
                                                    (tabRefs.current.transit =
                                                        el)
                                                }
                                            >
                                                대중교통
                                            </span>
                                        </label>
                                    </div> */}
                                    <SlidingRadioTabs
                                        name="travel"
                                        options={[
                                            { value: "car", label: "자동차" },
                                            { value: "walk", label: "도보" },
                                            {
                                                value: "transit",
                                                label: "대중교통",
                                            },
                                        ]}
                                        value={travelMode}
                                        onChange={(v) => {
                                            setTravelMode(v);
                                            setRouteData(null);
                                        }}
                                        deps={[
                                            isPanelOpen,
                                            infoIndex,
                                            isMobile,
                                            dockOpen,
                                        ]}
                                    />
                                    <button
                                        className="routeBtn"
                                        onClick={() => requestRoute(travelMode)}
                                    >
                                        {content?.mapRouteBtn || "경로찾기"} ›
                                    </button>
                                </div>
                            </div>
                            {directionResults.length > 0 && (
                                <ul className="directionsResultsBox">
                                    {directionResults.map((item, idx) => (
                                        <li
                                            key={idx}
                                            onClick={() =>
                                                handleDirectionSelect(item)
                                            }
                                            className="directionsResults"
                                        >
                                            <div className="resultsInpoText">
                                                <h3>
                                                    {item.title.replace(
                                                        /<[^>]*>/g,
                                                        ""
                                                    )}
                                                </h3>
                                                <p>{item.roadAddress}</p>
                                            </div>
                                            <div className="resultsInpoText">
                                                <button>
                                                    {directionMode === "start"
                                                        ? (content?.mapStart || "출발")
                                                        : (content?.mapEnd || "도착")}
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {routeDistance && routeDuration && (
                                <div className="route-summary">
                                    <div className="routeDurationBox">
                                        <p className="routeDurationhour">
                                            {(() => {
                                                const hours = Math.floor(
                                                    routeDuration / 3600
                                                ); // 초 → 시간
                                                const minutes = Math.floor(
                                                    (routeDuration % 3600) / 60
                                                );
                                                const seconds =
                                                    routeDuration % 60;
                                                return (
                                                    <>
                                                        {hours > 0 &&
                                                            `${hours}시간 `}
                                                        {minutes > 0 &&
                                                            `${minutes}분 `}
                                                        {seconds > 0 &&
                                                            `${seconds}초`}
                                                    </>
                                                );
                                            })()}
                                        </p>
                                        <p className="routeDurationkm">
                                            {(routeDistance / 1000).toFixed(1)}
                                            km
                                        </p>
                                    </div>
                                    <button
                                        className="detailed-route-btn"
                                        onClick={() =>
                                            setShowDetails((v) => !v)
                                        }
                                        aria-expanded={showDetails}
                                    >
                                        {content?.mapDetailRoute || "상세 경로"}
                                        <img
                                            src="/img/icon_map_detailed-route-btn.png"
                                            alt={
                                                showDetails
                                                    ? "닫힘 버튼"
                                                    : "열림 버튼"
                                            }
                                        />
                                    </button>
                                </div>
                            )}

                            {carSteps.length > 0 && showDetails && (
                                <div className="route-details">
                                    <ol>
                                        {carSteps.map((step, index) => {
                                            const { icon, label } = getTurnMeta(
                                                step.turnType || null
                                            );
                                            const desc = step.turnType
                                                ? step.description
                                                : `${step.description} 이동`;

                                            const hasDistanceInfo =
                                                typeof step.distance ===
                                                    "number" &&
                                                typeof step.duration ===
                                                    "number";

                                            return (
                                                <li
                                                    key={index}
                                                    className="car-detailed-route"
                                                >
                                                    <img
                                                        src={icon}
                                                        alt={label}
                                                    />
                                                    <div className="car-detailed-route-info">
                                                        <p
                                                            dangerouslySetInnerHTML={{
                                                                __html: desc,
                                                            }}
                                                        />

                                                        {hasDistanceInfo && (
                                                            <span>
                                                                (
                                                                {(
                                                                    step.distance /
                                                                    1000
                                                                ).toFixed(2)}
                                                                km,{" "}
                                                                {Math.round(
                                                                    step.duration /
                                                                        60
                                                                )}
                                                                분)
                                                            </span>
                                                        )}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ol>
                                </div>
                            )}

                            {walkSteps.length > 0 && showDetails && (
                                <div className="route-details">
                                    <ol>
                                        {walkSteps.map((step, index) => {
                                            const desc = step.turnType
                                                ? step.description
                                                : `${step.description} 이동`;

                                            return (
                                                <li
                                                    key={index}
                                                    className="walk-detailed-route"
                                                >
                                                    <span
                                                        dangerouslySetInnerHTML={{
                                                            __html: desc,
                                                        }}
                                                    />
                                                </li>
                                            );
                                        })}
                                    </ol>
                                </div>
                            )}

                            {transitSteps.length > 0 && showDetails && (
                                <div className="route-details">
                                    <ol>{transitSteps.map(renderStep)}</ol>
                                </div>
                            )}
                        </div>
                    )}
                    {/* 위치검색 탭 (index 2) */}
                    {infoIndex === 2 && (
                        <div>
                            <div className="searchinputWrap">
                                {isMobile && isPanelOpen && (
                                    <button
                                        className="panelBackBtn"
                                        onClick={() => {
                                            setMobileStage("home");
                                            setIsPanelOpen(false);
                                        }}
                                    >
                                        ‹
                                    </button>
                                )}
                                <div className="searchInput">
                                    <input
                                        type="text"
                                        value={searchAddress}
                                        onChange={(e) =>
                                            setSearchAddress(e.target.value)
                                        }
                                        placeholder={content?.mapSearchPlaceholder || "주소 검색"}
                                    />
                                    <button
                                        onClick={() =>
                                            handleSearch(searchAddress, "default")
                                        }
                                    >
                                        <img src="/img/icon_map_search.png" />
                                    </button>
                                </div>
                            </div>
                            <div className="searchResultsWrap">
                                <ul>
                                    {searchResults.map((item, idx) => (
                                        <li
                                            key={idx}
                                            onClick={(e) => {
                                                if (e.target.closest("button")) return;
                                                moveTo(item.lat, item.lng);
                                                if (isMobile) {
                                                    setMobileStage("home");
                                                    setIsPanelOpen(false);
                                                }

                                            }}
                                            className="searchResults"
                                        >
                                            <h3>
                                                {item.title.replace(/<[^>]*>/g, "")}
                                            </h3>
                                            <p>{item.roadAddress}</p>
                                            <div>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setAsStart(item);
                                                    }}
                                                >
                                                    {content?.mapStart || "출발"}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setAsEnd(item);
                                                    }}
                                                >
                                                    {content?.mapEnd || "도착"}
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
                <button
                    className="panelToggleBtn"
                    onClick={() => setIsPanelOpen((v) => !v)}
                >
                </button>
            </div>

            <div className="mapContainer">
                <div ref={mapRef} className="map" style={{ width: "100%" }} />
            </div>
            
            {/* 모바일 하단 버튼 */}
            {isMobile && !isPanelOpen && (
                <div className={`mobileDock ${dockOpen ? "open" : "closed"}`}>
                    <button
                        className="dockHandle"
                        onClick={() => setDockOpen((v) => !v)}
                        aria-label={dockOpen ? "도크 닫기" : "도크 열기"}
                    >
                        {dockOpen ? "▾" : "▴"}
                    </button>
                    {dockOpen && (
                        <div>
                            <div className="dockSearch">
                                <input
                                    type="text"
                                    value={searchAddress}
                                    onChange={(e) => setSearchAddress(e.target.value)}
                                    onClick={() => {
                                        setInfoIndex(2);
                                        setMobileStage("search");
                                        setIsPanelOpen(true);
                                    }}
                                    placeholder="주소 검색"
                                />
                            </div>
                            <div className="menu menu--mobile">
                                {infoMenuList.map((item, index) => (
                                    <div
                                        key={item.id}
                                        className={`tab tab-${item.id} ${
                                            infoIndex === index ? "active" : ""
                                        }`}
                                        onClick={() => {
                                            setInfoIndex(index);
                                            if (index === 0) {
                                                setMobileStage("layers");
                                            } else if (index === 1) {
                                                setMobileStage("directions");
                                            } else {
                                                setMobileStage("search");
                                            }
                                            setIsPanelOpen(true);
                                        }}
                                    >
                                        {item.title}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Map;
